/**
 * PatientTreatmentJourney — WORKFLOW-OPS-001-PHASE-2B
 * ====================================================
 *
 * Read-only patient view of canonical workflow data. Replaces the
 * hard-coded "Offer Accepted → Payment → Documents & Travel → Treatment
 * → Post-Treatment Care" timeline that was on PatientDashboard.
 *
 * Source of truth (P0-A / P0-B):
 *   GET /api/cases                                       → resolve case_id
 *   GET /api/cases/{case_id}/patient-journey             → sanitized
 *                                                          milestones + tasks
 *
 * Strict rules (P0-E role safety — enforced both BE and FE):
 *   - never display assignee user_id
 *   - never display assignee role
 *   - never display admin status names
 *   - no assign / reassign / transfer / status-picker controls
 *   - no "history" affordance
 *   - read-only end-to-end
 *
 * Patient status language (P0-D):
 *   in_progress → "In progress"
 *   achieved    → "Completed"
 *   blocked     → "Waiting for action"
 *   pending     → "Upcoming"
 *   scheduled   → "Scheduled"
 */
import React, { useEffect, useMemo, useState } from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  HourglassIcon,
  ChevronDown,
  ChevronUp,
  FileText,
  PenLine,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { api } from '../utils/api';

const STAGE_VARIANT = {
  Completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'In progress': 'bg-amber-100 text-amber-700 border-amber-300',
  'Waiting for action': 'bg-red-100 text-red-700 border-red-300',
  Upcoming: 'bg-slate-100 text-slate-600 border-slate-300',
  Scheduled: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  Skipped: 'bg-slate-50 text-slate-400 border-slate-200',
  Cancelled: 'bg-slate-50 text-slate-400 border-slate-200',
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return formatDateLocale(iso);
  } catch {
    return null;
  }
}

function StageDot({ stage }) {
  if (stage.is_completed) {
    return (
      <div className="absolute left-2 w-5 h-5 rounded-full bg-emerald-500 border-2 border-emerald-500 flex items-center justify-center">
        <CheckCircle2 className="h-3 w-3 text-white" />
      </div>
    );
  }
  if (stage.is_active && stage.is_waiting_on_action) {
    return (
      <div className="absolute left-2 w-5 h-5 rounded-full bg-red-500 border-2 border-red-500 flex items-center justify-center">
        <AlertCircle className="h-3 w-3 text-white" />
      </div>
    );
  }
  if (stage.is_active) {
    return (
      <div className="absolute left-2 w-5 h-5 rounded-full bg-amber-500 border-2 border-amber-500 flex items-center justify-center">
        <HourglassIcon className="h-3 w-3 text-white" />
      </div>
    );
  }
  return (
    <div className="absolute left-2 w-5 h-5 rounded-full bg-white border-2 border-slate-300" />
  );
}

function OutstandingRequirement({ req, onSatisfied, taskId, caseId }) {
  const { t } = useTranslation();
  const Icon = req.kind === 'signature' ? PenLine : FileText;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const fileInputRef = React.useRef(null);
  const [showConsent, setShowConsent] = useState(false);
  const [typedName, setTypedName] = useState('');

  // WORKFLOW-OPS-001-PHASE-3A — satisfy a document requirement by
  // uploading the file via the canonical patient-files endpoint, then
  // binding the resulting attachment_id to the precondition.
  const satisfyDocument = async (file) => {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('files', file);
      const { data: upload } = await api.post('/patient/medical-files', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const att = (upload?.files || [])[0];
      if (!att?.attachment_id) {
        throw new Error(upload?.files?.[0]?.error || 'Upload failed');
      }
      await api.post(
        `/cases/${caseId}/patient-tasks/${taskId}/requirements/${req.requirement_id}/satisfy`,
        { satisfied_by_doc_id: att.attachment_id },
      );
      onSatisfied && onSatisfied();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.response?.data?.message || e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const satisfyConsent = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(
        `/cases/${caseId}/patient-tasks/${taskId}/requirements/${req.requirement_id}/satisfy`,
        {
          consent_acknowledged: true,
          typed_signature: typedName || null,
        },
      );
      onSatisfied && onSatisfied();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.response?.data?.message || e.message || 'Failed');
    } finally {
      setBusy(false);
      setShowConsent(false);
    }
  };

  return (
    <li
      className="flex flex-col gap-1 py-1.5 border-b border-slate-100 last:border-b-0"
      data-testid={`patient-outstanding-${req.requirement_id}`}
    >
      <div className="flex items-start gap-2">
        <Icon className="h-3.5 w-3.5 text-slate-500 mt-0.5 shrink-0" />
        <span className="text-sm text-slate-700">{req.title}</span>
        <span className="text-[11px] text-slate-400 ml-auto">
          {req.kind === 'signature' ? 'Signature needed' : 'Document needed'}
        </span>
      </div>
      <div className="ml-5">
        {req.kind === 'document' ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => satisfyDocument(e.target.files?.[0])}
              data-testid={`patient-req-${req.requirement_id}-file-input`}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
              className="text-xs font-medium px-2 py-1 rounded border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60"
              data-testid={`patient-req-${req.requirement_id}-upload`}
            >
              {busy ? 'Uploading…' : 'Upload to satisfy'}
            </button>
          </>
        ) : showConsent ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <input
              type="text"
              placeholder={t('patient.journey.typeYourName')}
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              className="text-xs border border-slate-300 rounded px-1.5 py-1"
              data-testid={`patient-req-${req.requirement_id}-typed-name`}
            />
            <button
              type="button"
              disabled={busy}
              onClick={satisfyConsent}
              className="text-xs font-medium px-2 py-1 rounded border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60"
              data-testid={`patient-req-${req.requirement_id}-consent-confirm`}
            >
              {busy ? 'Recording…' : 'I consent'}
            </button>
            <button
              type="button"
              onClick={() => setShowConsent(false)}
              className="text-xs text-slate-500 hover:text-slate-700 px-1"
              data-testid={`patient-req-${req.requirement_id}-consent-cancel`}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowConsent(true)}
            className="text-xs font-medium px-2 py-1 rounded border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
            data-testid={`patient-req-${req.requirement_id}-consent`}
          >
            Provide consent
          </button>
        )}
        {err && (
          <span className="ml-2 text-[11px] text-red-600" data-testid={`patient-req-${req.requirement_id}-error`}>
            {err}
          </span>
        )}
      </div>
    </li>
  );
}

export default function PatientTreatmentJourney() {
  const { t } = useTranslation();
  const [cases, setCases] = useState([]);
  const [activeCaseId, setActiveCaseId] = useState(null);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);
  // WORKFLOW-OPS-001-PHASE-3A: reload trigger after a requirement is
  // satisfied so the patient sees the binding state immediately.
  const [refreshTick, setRefreshTick] = useState(0);
  const reload = () => setRefreshTick((t) => t + 1);

  // 1. Resolve patient's case(s) via the canonical (now patient-aware) endpoint.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await api.get('/cases');
        if (cancelled) return;
        const list = Array.isArray(data?.cases) ? data.cases : [];
        setCases(list);
        // Pick the most recent case (cases are returned sorted DESC by
        // created_at on the backend).
        setActiveCaseId(list.length ? list[0].case_id : null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message || 'Failed to load your cases');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  // 2. Pull patient-safe journey projection for the active case.
  useEffect(() => {
    if (!activeCaseId) {
      setJourney(null);
      return;
    }
    let cancelled = false;
    setJourneyLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/cases/${activeCaseId}/patient-journey`);
        if (!cancelled) setJourney(data);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e.message || 'Failed to load your treatment journey');
          setJourney(null);
        }
      } finally {
        if (!cancelled) setJourneyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCaseId, refreshTick]);

  const stages = journey?.stages || [];
  const summary = journey?.summary || {};

  const outstandingFlat = useMemo(() => {
    const out = [];
    stages.forEach((s) => {
      (s.tasks || []).forEach((t) => {
        (t.outstanding_requirements || []).forEach((r) => {
          out.push({ ...r, stage_title: s.title, task_title: t.title, task_id: t.task_id });
        });
      });
    });
    return out;
  }, [stages]);

  if (loading) {
    return (
      <Card className="mb-4" data-testid="patient-journey-loading">
        <CardContent className="py-6 text-center text-sm text-slate-500">
          Loading your treatment journey…
        </CardContent>
      </Card>
    );
  }

  // POST-PAYMENT-TRUTH-01 — a FAILED `/cases` read left `cases` empty and fell
  // into the empty state below, telling the patient "You have no active
  // treatment cases yet" when the truth was "we could not load them".
  if (error && !cases.length) {
    return (
      <Card className="mb-4 border-amber-200 bg-amber-50/60" data-testid="patient-journey-cases-error">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-700">{t('patient.journey.treatmentJourney')}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-700">
          <AlertCircle className="h-5 w-5 text-amber-600 mb-2" />
          <p className="mb-3">{error}</p>
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
            onClick={() => setRefreshTick((n) => n + 1)}
            data-testid="patient-journey-cases-retry"
          >
            {t('patient.dashboard.tryAgain')}
          </button>
        </CardContent>
      </Card>
    );
  }

  // No case yet → show a polite empty state. Do NOT render the hard-coded
  // placeholder timeline (that was the audit finding we're closing).
  if (!cases.length || !activeCaseId) {
    return (
      <Card
        className="mb-4 border-slate-200 bg-slate-50"
        data-testid="patient-journey-empty"
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-700">{t('patient.journey.treatmentJourney')}</CardTitle>
          <CardDescription className="text-xs text-slate-500">
            Your treatment journey will appear here once a clinic begins your case.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-slate-500">
          You have no active treatment cases yet. After you accept an offer, the
          journey will populate from your clinic's actual treatment plan.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="mb-4 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white"
      data-testid="patient-journey-card"
    >
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle
              className="text-base flex items-center gap-2 text-emerald-800"
              data-testid="patient-journey-title"
            >
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Treatment Journey
            </CardTitle>
            <CardDescription
              className="text-xs text-emerald-700/80 mt-1"
              data-testid="patient-journey-treatment-name"
            >
              {journey?.case?.treatment_name || 'Your treatment'}
              {journey?.case?.status_label && (
                <>
                  <span className="mx-1">·</span>
                  <span className="font-medium">{journey.case.status_label}</span>
                </>
              )}
            </CardDescription>
          </div>
          {cases.length > 1 && (
            <select
              value={activeCaseId || ''}
              onChange={(e) => setActiveCaseId(e.target.value)}
              className="text-sm border border-slate-300 rounded px-2 py-1 bg-white"
              data-testid="patient-journey-case-picker"
            >
              {cases.map((c) => (
                <option key={c.case_id} value={c.case_id}>
                  {c.treatment_display_name || c.treatment_name || c.case_id}
                </option>
              ))}
            </select>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {journeyLoading ? (
          <div className="py-6 text-center text-sm text-slate-500" data-testid="patient-journey-journey-loading">
            Loading stages…
          </div>
        ) : error ? (
          <div className="py-6 text-center text-sm text-red-600" data-testid="patient-journey-error">
            <AlertCircle className="h-5 w-5 mx-auto mb-1" />
            {error}
          </div>
        ) : (
          <>
            {/* Top summary chips */}
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="patient-journey-summary">
              <Badge variant="outline" className="bg-white text-slate-600 border-slate-300">
                {summary.stage_count || 0} stage{summary.stage_count === 1 ? '' : 's'}
              </Badge>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300" data-testid="patient-journey-completed-chip">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {summary.completed_count || 0} completed
              </Badge>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300" data-testid="patient-journey-active-chip">
                <Clock className="h-3 w-3 mr-1" />
                {summary.active_count || 0} in progress
              </Badge>
              <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300" data-testid="patient-journey-upcoming-chip">
                {summary.upcoming_count || 0} upcoming
              </Badge>
              {summary.outstanding_total > 0 && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300" data-testid="patient-journey-outstanding-chip">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {summary.outstanding_total} action{summary.outstanding_total === 1 ? '' : 's'} needed
                </Badge>
              )}
            </div>

            {/* Stages — vertical timeline */}
            <div className="relative" data-testid="patient-journey-stages">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-emerald-200" />
              {stages.length === 0 ? (
                <p className="text-sm text-slate-500 pl-10 py-2">
                  Your clinic has not yet defined the treatment stages.
                </p>
              ) : (
                stages.map((s, idx) => {
                  const expanded = expandedStage === s.milestone_instance_id;
                  const labelCls = STAGE_VARIANT[s.stage_label] || STAGE_VARIANT.Upcoming;
                  return (
                    <div
                      key={s.milestone_instance_id || idx}
                      className="relative pl-10 pb-4 last:pb-0"
                      data-testid={`patient-stage-${idx}`}
                    >
                      <StageDot stage={s} />
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p
                            className={`text-sm font-medium ${s.is_completed ? 'text-emerald-800' : s.is_active ? 'text-amber-900' : 'text-slate-600'}`}
                            data-testid={`patient-stage-${idx}-title`}
                          >
                            {s.title}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border ${labelCls}`}
                              data-testid={`patient-stage-${idx}-label`}
                            >
                              {s.stage_label}
                            </span>
                            {s.is_waiting_on_action && !s.is_completed && (
                              <span className="text-[11px] text-red-600 font-medium" data-testid={`patient-stage-${idx}-waiting`}>
                                Waiting on action
                              </span>
                            )}
                            {fmtDate(s.completed_at) && (
                              <span className="text-[11px] text-slate-400">{fmtDate(s.completed_at)}</span>
                            )}
                            {!s.completed_at && fmtDate(s.scheduled_date) && (
                              <span className="text-[11px] text-slate-400">Scheduled {fmtDate(s.scheduled_date)}</span>
                            )}
                          </div>
                        </div>
                        {(s.tasks || []).length > 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedStage(expanded ? null : s.milestone_instance_id)
                            }
                            className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center"
                            data-testid={`patient-stage-${idx}-toggle`}
                          >
                            {expanded ? (
                              <>
                                Less <ChevronUp className="h-3 w-3 ml-0.5" />
                              </>
                            ) : (
                              <>
                                Details <ChevronDown className="h-3 w-3 ml-0.5" />
                              </>
                            )}
                          </button>
                        )}
                      </div>

                      {expanded && (
                        <div
                          className="mt-2 ml-1 rounded border border-slate-200 bg-white p-2 space-y-2"
                          data-testid={`patient-stage-${idx}-details`}
                        >
                          {(s.tasks || []).map((t) => (
                            <div
                              key={t.task_id}
                              className="border-l-2 border-slate-200 pl-3 py-1"
                              data-testid={`patient-task-${t.task_id}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm text-slate-700">{t.title}</span>
                                <span
                                  className={`text-[11px] px-1.5 py-0.5 rounded border ${STAGE_VARIANT[t.status_label] || STAGE_VARIANT.Upcoming}`}
                                  data-testid={`patient-task-${t.task_id}-label`}
                                >
                                  {t.status_label}
                                </span>
                              </div>
                              {t.outstanding_count > 0 && (
                                <ul className="mt-1" data-testid={`patient-task-${t.task_id}-requirements`}>
                                  {t.outstanding_requirements.map((r) => (
                                    <OutstandingRequirement
                                      key={r.requirement_id}
                                      req={r}
                                      taskId={t.task_id}
                                      caseId={activeCaseId}
                                      onSatisfied={reload}
                                    />
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Outstanding requirements consolidated panel (P0-C) */}
            {outstandingFlat.length > 0 && (
              <div
                className="mt-4 rounded-lg border border-red-200 bg-red-50/60 p-3"
                data-testid="patient-outstanding-panel"
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <h4 className="text-sm font-semibold text-red-800">
                    Outstanding requirements ({outstandingFlat.length})
                  </h4>
                </div>
                <p className="text-xs text-red-700/80 mb-2">
                  Your clinic needs the following from you to move your treatment
                  forward. Use the Medical Files and My Consents tabs above to
                  provide them.
                </p>
                <ul className="divide-y divide-red-200/60">
                  {outstandingFlat.map((r) => (
                    <li
                      key={`${r.task_id}-${r.requirement_id}`}
                      className="py-2"
                      data-testid={`patient-outstanding-flat-${r.requirement_id}`}
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        {r.kind === 'signature' ? (
                          <PenLine className="h-3.5 w-3.5 text-red-600" />
                        ) : (
                          <FileText className="h-3.5 w-3.5 text-red-600" />
                        )}
                        <span className="text-sm text-slate-700">{r.title}</span>
                        <span className="text-[11px] text-slate-500">
                          on stage <span className="font-medium">{r.stage_title}</span>
                        </span>
                      </div>
                      <div className="pl-5">
                        <OutstandingRequirement
                          req={r}
                          taskId={r.task_id}
                          caseId={activeCaseId}
                          onSatisfied={reload}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
